<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:show', 'chatCreated']);

const show = computed({
  get: () => props.show,
  set: val => emit('update:show', val),
});

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

const search = ref('');
// [polish] track loading state locally (isFetchingChats is for the chat list, not agents)
const isFetchingAgents = ref(false);
let debounceTimer = null;

// BUG-1 (audit 2026-06-16): exclude the current user — you cannot DM yourself
// (the backend rejects it with 422), so it must not be offered in the list.
const currentUserId = computed(() => store.getters.getCurrentUser?.id);
const agents = computed(() =>
  (store.getters['internalChats/getAgentsCache'] || []).filter(
    a => a.id !== currentUserId.value
  )
);

const filteredAgents = computed(() => {
  const q = search.value.toLowerCase();
  if (!q) return agents.value;
  return agents.value.filter(
    a =>
      a.name.toLowerCase().includes(q) ||
      (a.email && a.email.toLowerCase().includes(q))
  );
});

const onSearch = () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(async () => {
    isFetchingAgents.value = true;
    try {
      await store.dispatch('internalChats/fetchAgents', search.value);
    } finally {
      isFetchingAgents.value = false;
    }
  }, 300);
};

const selectAgent = async agent => {
  try {
    const chat = await store.dispatch('internalChats/createDm', agent.id);
    emit('chatCreated', chat);
    show.value = false;
  } catch (error) {
    showAlert(
      error?.response?.data?.error || t('INTERNAL_CHATS.TOAST.ERROR_GENERIC')
    );
  }
};

const onClose = () => {
  search.value = '';
};

// [polish] clear debounce timer on unmount to prevent memory leak
onBeforeUnmount(() => clearTimeout(debounceTimer));

watch(
  () => props.show,
  async val => {
    if (val) {
      isFetchingAgents.value = true;
      try {
        await store.dispatch('internalChats/fetchAgents', '');
      } finally {
        isFetchingAgents.value = false;
      }
    }
  }
);
</script>

<template>
  <Modal v-model:show="show" @close="onClose">
    <div class="p-6 flex flex-col gap-4">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('INTERNAL_CHATS.NEW_DM') }}
      </h3>
      <input
        v-model="search"
        type="text"
        name="dm-agent-search"
        :aria-label="t('INTERNAL_CHATS.SEARCH_AGENT')"
        :placeholder="t('INTERNAL_CHATS.SEARCH_AGENT')"
        class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand placeholder:text-n-slate-9"
        @input="onSearch"
      />
      <div class="flex flex-col gap-1 max-h-60 overflow-y-auto">
        <!-- [polish] local loading state (isFetchingChats was wrong flag) -->
        <div v-if="isFetchingAgents" class="py-4 flex justify-center">
          <span class="i-lucide-loader-2 size-4 text-n-slate-9 animate-spin" />
        </div>
        <template v-else>
          <button
            v-for="agent in filteredAgents"
            :key="agent.id"
            class="flex items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-n-alpha-1 transition-colors"
            @click="selectAgent(agent)"
          >
            <Avatar
              :name="agent.name"
              :src="agent.avatar_url"
              :size="32"
              rounded-full
            />
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ agent.name }}
              </p>
              <p class="text-xs text-n-slate-9">{{ agent.email }}</p>
            </div>
          </button>
          <p
            v-if="filteredAgents.length === 0"
            class="py-4 text-center text-sm text-n-slate-9"
          >
            {{
              search
                ? t('INTERNAL_CHATS.NO_SEARCH_RESULTS')
                : t('INTERNAL_CHATS.EMPTY_LIST')
            }}
          </p>
        </template>
      </div>
    </div>
  </Modal>
</template>
